import { createFileRoute, Link } from "@tanstack/react-router";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { pageHead, breadcrumbJsonLd, webPageJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const PATH = "/terms";
const TITLE = "Terms of Use — ReadBarcode.com";
const DESC =
  "Read the ReadBarcode.com Terms of Use covering barcode tools, validation limits, generated labels, GS1 disclaimer, prohibited use, liability, governing law, and platform access.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Terms", path: PATH },
];

const EFFECTIVE_DATE = "2026-05-25";
const LAST_UPDATED = "2026-05-25";
const BREADCRUMB_ID = `${absoluteUrl(PATH)}#breadcrumb`;

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => ({
    ...pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
      ogImage: absoluteUrl("/og/terms.jpg"),
      ogImageAlt:
        "ReadBarcode.com Terms of Use — barcode tools, validation limits, GS1 disclaimer, prohibited use, liability and governing law.",
    }),
    scripts: [
      ldScript(
        webPageJsonLd({
          path: PATH,
          title: TITLE,
          description: DESC,
          datePublished: EFFECTIVE_DATE,
          dateModified: LAST_UPDATED,
          breadcrumbId: BREADCRUMB_ID,
        }),
      ),
      ldScript(breadcrumbJsonLd(CRUMBS, BREADCRUMB_ID)),
    ],
  }),
});

function TermsPage() {
  return (
    <ToolLayout
      eyebrow="Legal"
      title="Terms of Use"
      description="The rules for using ReadBarcode.com — barcode tools, validation limits, generated labels, GS1 disclaimer, prohibited use, liability and governing law. By using the site you accept these terms, which apply to every free tool, generated barcode, printed label and download on ReadBarcode.com."
      crumbs={CRUMBS}
      content={
        <>
          <p>
            {`Effective date: ${formatDate(EFFECTIVE_DATE)} · Last updated: ${formatDate(LAST_UPDATED)}`}
          </p>
          <p>
            These Terms of Use (the &ldquo;Terms&rdquo;) govern your access to and use of the website
            at <strong>https://readbarcode.com</strong> and all tools, generators, validators, guides,
            the installable Progressive Web App, and any related content offered there (together, the
            &ldquo;platform&rdquo; or the &ldquo;service&rdquo;). Use of the platform is also governed
            by our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms
            by reference.
          </p>

          <h2>Acceptance of These Terms</h2>
          <p>
            By accessing or using ReadBarcode.com, you agree to be bound by these Terms of Use. If you
            do not agree to these Terms, you may not use the platform. These Terms apply to all
            visitors, users, and others who access or use the service.
          </p>
          <p>
            By continuing to access or use ReadBarcode.com after we post changes to these Terms, you
            agree to be bound by the revised Terms. If you do not agree to the revised Terms, you must
            stop using the platform.
          </p>

          <h2>What ReadBarcode.com Provides</h2>
          <p>
            ReadBarcode.com provides barcode reading, generation, validation, formatting, printing,
            export, and educational tools for informational and practical utility purposes. The
            platform groups into five things:
          </p>
          <ul>
            <li>
              <strong>Readers</strong> — <Link to="/barcode-reader">barcode reader</Link>, <Link to="/qr-code-reader">QR reader</Link>, <Link to="/data-matrix-reader">Data Matrix reader</Link>, <Link to="/read-barcode-from-image">read from image</Link>.
            </li>
            <li>
              <strong>Generators</strong> — <Link to="/barcode-generator">barcode</Link>, <Link to="/qr-code-generator">QR</Link>, <Link to="/wifi-qr-code-generator">Wi-Fi QR</Link>, <Link to="/vcard-qr-generator">vCard QR</Link>, <Link to="/data-matrix-generator">Data Matrix</Link>, <Link to="/pdf417-generator">PDF417</Link>, <Link to="/aztec-code-generator">Aztec</Link>, <Link to="/itf-14-label-generator">ITF-14</Link>, and <Link to="/gs1-digital-link-generator">GS1 Digital Link</Link>.
            </li>
            <li>
              <strong>Lookups and validators</strong> — <Link to="/upc-lookup">UPC lookup</Link>, <Link to="/ean-lookup">EAN lookup</Link>, <Link to="/isbn-barcode-reader">ISBN reader</Link>, <Link to="/gtin-validator">GTIN validator</Link>, <Link to="/check-digit-calculator">check digit calculator</Link>, <Link to="/barcode-type-detector">barcode type detector</Link>.
            </li>
            <li>
              <strong>GS1 and inventory tooling</strong> — <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link>, <Link to="/inventory-scanner">inventory scanner</Link>, <Link to="/batch-barcode-scanner">batch barcode tools</Link>.
            </li>
            <li>
              <strong>Educational guides</strong> for barcode formats and standards — <Link to="/barcode-guides">barcode guides</Link>.
            </li>
          </ul>

          <h2>Free Tools and Future Monetization</h2>
          <p>
            ReadBarcode.com currently offers its core tools free of charge. In the future, the
            platform may introduce advertising, affiliate links, premium features, paid APIs, batch
            processing limits, subscriptions, or other monetization options. Any paid features will be
            clearly identified before any payment obligation arises.
          </p>
          <p>
            Some pages may include affiliate links, sponsored placements, advertising, or commercial
            recommendations in the future. Such monetization does not change the user&apos;s
            responsibility to verify barcode accuracy and suitability.
          </p>

          <h2>User Responsibilities</h2>
          <p>
            You are responsible for the data, values, images, labels, files, QR payloads, Wi-Fi
            details, vCard details, inventory lists, and other content you enter, generate, export, or
            use through the tools. In particular, you must:
          </p>
          <ul>
            <li>verify outputs before relying on them;</li>
            <li>only use the platform with data you are legally entitled to process;</li>
            <li>only generate or use barcodes you have the right to generate or use;</li>
            <li>avoid producing misleading, fraudulent, or counterfeit labels;</li>
            <li>test labels and exports before printing or commercial use;</li>
            <li>respect third-party rights, trademarks, and standards;</li>
            <li>not rely on the platform as the sole source for compliance decisions.</li>
          </ul>

          <h2>Accuracy and Barcode Validation Limitations</h2>
          <p>
            We aim to make the tools accurate and useful, but we do not guarantee that all decoding,
            validation, generation, lookup, printing, or export results will be error-free, complete,
            current, or suitable for every use case.
          </p>
          <p>
            You use ReadBarcode.com at your own risk. You are responsible for verifying all barcode
            output, validation results, printed labels, generated codes, exported files, and business
            decisions before relying on them.
          </p>
          <p>
            A valid barcode or check digit does not prove product ownership, GS1 registration, brand
            authorization, product authenticity, marketplace approval, retailer acceptance, or legal
            compliance.
          </p>
          <p>
            Before using barcode results in retail, logistics, healthcare, manufacturing, legal,
            compliance, packaging, inventory, or commercial environments, you should independently
            verify the result with the appropriate official source, retailer, marketplace, standards
            body, or qualified professional.
          </p>
          <p>
            Barcode lookup and structure tools may help identify barcode format, check digit validity,
            or encoded structure. They should not be treated as official product ownership or product
            database verification unless a specific external data source is clearly identified.
          </p>
          <p>
            ReadBarcode.com does not provide legal, regulatory, compliance, retail certification,
            medical, manufacturing, logistics, or professional advice.
          </p>

          <h2>Printing, Labels, Exports and Production Use</h2>
          <p>
            Generated barcode labels, print layouts, SVG files, PNG files, and PDFs should be tested
            with the intended scanner, printer, paper, label stock, and retail or operational workflow
            before production use.
          </p>

          <h2>GS1 and Third-Party Standards Disclaimer</h2>
          <p>
            ReadBarcode.com is independent and is not affiliated with, endorsed by, sponsored by,
            certified by, or operated by GS1. GS1 names, standards, identifiers, Application
            Identifiers, Digital Link terminology, and related references are used only for
            compatibility, education, and barcode-format explanation.
          </p>

          <h2>Prohibited Uses</h2>
          <p>You may not use ReadBarcode.com to:</p>
          <ul>
            <li>
              use generated codes for illegal, deceptive, harmful, abusive, or fraudulent activity;
            </li>
            <li>generate misleading, fraudulent, counterfeit, or unauthorized barcode labels;</li>
            <li>
              create or distribute deceptive product identifiers, fake product labels, or misleading
              packaging;
            </li>
            <li>
              impersonate brands, manufacturers, retailers, certification bodies, GS1, product owners,
              or other organizations;
            </li>
            <li>
              violate intellectual property, trademark, privacy, publicity, or contractual rights;
            </li>
            <li>use the platform in a way that violates applicable laws or regulations;</li>
            <li>
              upload, process, or distribute malicious code, harmful files, or unlawful content;
            </li>
            <li>
              bypass, disable, or interfere with rate limits, access controls, security systems, or
              usage restrictions;
            </li>
            <li>abuse, overload, scrape, attack, spam, or disrupt the service;</li>
            <li>reverse engineer, clone, copy, or exploit the platform for competitive misuse.</li>
          </ul>

          <h2>Automation, Scraping and Abuse</h2>
          <p>
            You may not use bots, scripts, crawlers, scrapers, automated requests, or bulk access
            patterns that overload the service, bypass limitations, copy content, or disrupt normal
            operation, unless we explicitly provide a public API or written permission.
          </p>

          <h2>Intellectual Property</h2>
          <p>
            All content, design, software, interface elements, guides, branding, and platform
            materials on ReadBarcode.com are owned by ReadBarcode.com or its operator, except for
            third-party names, standards, trademarks, or references used for identification and
            compatibility.
          </p>

          <h2>Privacy and User Data</h2>
          <p>
            Your privacy is covered by the <Link to="/privacy">Privacy Policy</Link>, which forms part
            of these Terms. In summary: barcode reading, generation, validation, and most tool inputs
            are processed in your browser and are not uploaded to our servers for normal tool use.
            Information you choose to send us through the Contact page is handled as described in the
            Privacy Policy.
          </p>

          <h2>Third-Party Links and Services</h2>
          <p>
            ReadBarcode.com may link to external websites, standards bodies, documentation, or
            third-party resources. We are not responsible for the content, policies, accuracy, or
            practices of external websites.
          </p>

          <h2>Availability and Changes</h2>
          <p>
            We may update, modify, suspend, limit, or discontinue any part of ReadBarcode.com at any
            time, with or without notice. We may also add, remove, rename, limit, or change tools,
            formats, features, routes, outputs, and documentation as the platform evolves.
          </p>

          <h2>Disclaimer of Warranties</h2>
          <p>
            ReadBarcode.com is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without
            warranties of any kind, either express or implied, including but not limited to implied
            warranties of merchantability, fitness for a particular purpose, non-infringement,
            accuracy, completeness, reliability, or availability. We do not warrant that the service
            will be uninterrupted, error-free, secure, or free of viruses or other harmful components.
          </p>

          <h2>Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, ReadBarcode.com and its operator are
            not liable for indirect, incidental, special, consequential, punitive, business, data,
            revenue, inventory, compliance, printing, marketplace, or operational losses arising from
            or related to your use of the platform, even if advised of the possibility of such losses.
          </p>
          <p>
            In jurisdictions that do not allow the exclusion or limitation of certain damages, our
            liability is limited to the maximum extent permitted by law.
          </p>
          <p>
            In any case, the total aggregate liability of ReadBarcode.com and its operator arising out
            of or related to these Terms or your use of the platform, whether in contract, tort, or
            otherwise, shall not exceed one hundred US dollars ($100), or the total amount you have
            paid us in the twelve months preceding the claim, whichever is greater.
          </p>

          <h2>Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless ReadBarcode.com and its operator from
            and against any claims, liabilities, damages, losses, costs, expenses, or fees (including
            reasonable legal fees) arising out of or related to: (a) your use or misuse of the
            platform; (b) your violation of these Terms; (c) your violation of any third-party rights,
            including intellectual property or privacy rights; or (d) any content you submit,
            generate, or distribute through the platform.
          </p>
          <p>
            We reserve the right, at your expense, to assume the exclusive defense and control of any
            matter for which you are required to indemnify us, and you agree to cooperate with our
            defense of these claims.
          </p>

          <h2>Governing Law and Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of the jurisdiction where the operator of
            ReadBarcode.com is established, without regard to conflict of law principles.
          </p>
          <p>
            Any disputes arising out of or related to these Terms or your use of ReadBarcode.com will
            be resolved in the courts of competent jurisdiction in the operator&apos;s place of
            establishment, unless mandatory consumer protection laws in your country of residence
            require otherwise.
          </p>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision
            will be limited or eliminated to the minimum extent necessary so that the remaining Terms
            remain in full force and effect.
          </p>
          <p>
            For users located in the European Economic Area, nothing in these Terms affects mandatory
            consumer protection rights you have under the laws of your country of residence.
          </p>

          <h2>Force Majeure</h2>
          <p>
            We are not liable for failure to perform any obligation under these Terms to the extent
            such failure is caused by events beyond our reasonable control, including but not limited
            to acts of God, natural disasters, war, terrorism, riots, embargoes, acts of civil or
            military authorities, fire, floods, accidents, network or service outages, infrastructure
            provider failures, or labor disputes.
          </p>

          <h2>Severability and Entire Agreement</h2>
          <p>
            If any provision of these Terms is found by a court of competent jurisdiction to be
            invalid, illegal, or unenforceable, the remaining provisions will remain in full force and
            effect.
          </p>
          <p>
            These Terms, together with the Privacy Policy and any other notices published on
            ReadBarcode.com, constitute the entire agreement between you and the operator regarding
            your use of the platform, and supersede any prior agreements or communications.
          </p>

          <h2>Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time to reflect changes in our practices, services,
            or legal obligations. When we make material changes, we will update the &ldquo;Last
            updated&rdquo; date at the top of this page and, where appropriate, provide additional
            notice. Your continued use of the platform after changes are posted constitutes acceptance
            of the revised Terms.
          </p>

          <h2>Contact</h2>
          <p>
            For questions about these Terms, please use the <Link to="/contact">Contact page</Link> with the subject line &ldquo;Terms inquiry&rdquo;. For legal notices, copyright (DMCA)
            claims, or trademark concerns, please use the subject line &ldquo;Legal notice&rdquo; and
            include all information required by applicable law for us to evaluate and respond to your
            request.
          </p>
        </>
      }
    />
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
}
